import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ToastrService } from 'ngx-toastr';
import { FileSizePipe } from 'src/app/_pipes/file-size.pipe';
import { CustomerService } from 'src/app/_services/customer.service';
import { DocumentService } from 'src/app/_services/document.service';
import { FamilyMember, User } from 'src/app/_types/auth.types';
import { DocumentUploadRequest } from 'src/app/_types/document.types';
import {
  DocumentType,
  getDocumentTypeLabel,
} from 'src/app/enums/document-type.enum';

import { IconsModule } from '../../icons/icons.module';
import { SlideInModalComponent } from '../slide-in-modal/slide-in-modal.component';

@Component({
  selector: 'app-document-upload-modal',
  templateUrl: './document-upload-modal.component.html',
  styleUrls: ['./document-upload-modal.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    IconsModule,
    SlideInModalComponent,
    FileSizePipe,
  ],
})
export class DocumentUploadModalComponent implements OnInit {
  @Input() userId!: string;

  @Input() memberId?: string;

  /** Prefills the "Type de document" field; the agent can still change it. */
  @Input() type?: DocumentType;

  /**
   * Drops the customer and family-member selects — and the two lookups that
   * only feed them. Only meaningful when `userId` and `memberId` are both
   * provided, since the hidden controls still carry the upload's owner.
   */
  @Input() hideCustomerFields = false;

  /** Fires the stored document's id, for a host that wants to select it. */
  @Output() documentUploaded = new EventEmitter<number>();

  users: User[] = [];

  familyMembers: FamilyMember[] = [];

  ready: boolean = false;

  uploadedFile?: File;

  submitted = false;

  userInputDisabled = false;

  memberInputDisabled = false;

  formGroup: FormGroup = this.fb.group({
    docName: ['', Validators.required],
    userId: ['', Validators.required],
    memberId: ['', Validators.required],
    type: [null],
    notifyCustomer: [false],
  });

  constructor(
    private readonly customerService: CustomerService,
    private readonly fb: FormBuilder,
    private readonly toastrService: ToastrService,
    private readonly documentService: DocumentService
  ) {}

  ngOnInit(): void {
    if (!this.userId)
      throw new Error(
        "You must provide the userId for the user you're adding a document"
      );
    this.formGroup.controls['userId'].setValue(this.userId);
    this.formGroup.controls['userId'].disable();
    this.userInputDisabled = true;
    if (this.type) {
      this.formGroup.controls['type'].setValue(this.type);
    }
    if (this.memberId) {
      this.formGroup.controls['memberId'].setValue(this.memberId);
      this.formGroup.controls['memberId'].disable();
      this.memberInputDisabled = true;
    }
    if (this.hideCustomerFields) {
      // The hidden member select still carries a required control: without the
      // id the form could never be submitted, and nothing on screen would say why.
      if (!this.memberId)
        throw new Error('hideCustomerFields needs memberId to be provided');
      this.ready = true;
      return;
    }
    this.customerService
      ._getCustomerDetails({ entityId: this.userId, entityType: 'USER_ID' })
      .subscribe({
        next: (res) => {
          this.users = [res.data];
        },
      });
    this.getFamilyMembers(this.userId);
  }

  @ViewChild('modal') modal!: SlideInModalComponent;

  openModal() {
    this.modal.openModal();
  }

  closeModal() {
    this.modal.closeModal(false);
  }

  getFamilyMembers(userId: string) {
    this.customerService._getAllCustomerFamilyList({ userId }).subscribe({
      next: (res) => {
        this.familyMembers = res.data.familyMemberList;
        this.ready = true;
      },
    });
  }

  onFileUpload(event: Event) {
    [this.uploadedFile] = Array.from((event.target as HTMLInputElement).files!);
  }

  submit() {
    if (this.submitted || !this.uploadedFile || this.formGroup.invalid) return;
    this.submitted = true;
    const obj: DocumentUploadRequest = {
      ...this.formGroup.getRawValue(),
      doc: this.uploadedFile,
    };
    this.documentService.UploadDocument(obj).subscribe({
      next: (res) => {
        if (!res.error) {
          this.toastrService.success($localize`Document ajouté avec succès`);
          this.documentUploaded.emit(res.data.documentId);
          this.closeModal();
          this.resetForm();
        }
        this.submitted = false;
      },
      error: () => {
        this.toastrService.error(
          $localize`Une erreur est survenue. Veuillez réessayer`
        );
        this.submitted = false;
      },
    });
  }

  resetForm() {
    if (!this.memberId) {
      this.formGroup.controls['memberId'].reset();
      this.familyMembers = [];
    }
    if (!this.userId) {
      this.formGroup.controls['userId'].reset();
    }
    this.uploadedFile = undefined;
    this.formGroup.controls['docName'].reset();
    this.formGroup.controls['type'].reset(this.type ?? null);
    this.formGroup.controls['notifyCustomer'].reset(false);
  }

  documentTypeValues = Object.values(DocumentType);

  getDocumentTypeLabel = getDocumentTypeLabel;
}
